import React, { useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { authAPI, testBackendConnection } from '../../services/api';

const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [backendStatus, setBackendStatus] = useState(null);
  const navigate = useNavigate();

  React.useEffect(() => {
    // Check if already logged in
    if (authAPI.isAuthenticated()) {
      navigate('/admin/dashboard');
    }
    
    // Test backend connection
    testBackendConnection().then(setBackendStatus);
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Attempting login with:', username);
      
      const result = await authAPI.login(username, password);
      console.log('Login result:', result);
      
      if (result && result.success) {
        // Store token and user data
        localStorage.setItem('admin_token', result.token);
        localStorage.setItem('admin_user', JSON.stringify(result.user));
        
        console.log('Login successful, redirecting...');
        navigate('/admin/dashboard');
      } else {
        setError(result?.message || 'Login failed. Please check credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'Network error. Please check backend connection.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page bg-light min-vh-100 d-flex align-items-center">
      <Container>
        <Row className="justify-content-center">
          <Col md={6} lg={4}>
            {/* Backend Status */}
            {backendStatus && (
              <Alert 
                variant={backendStatus.success ? 'success' : 'danger'} 
                className="mb-3"
              >
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Backend Status:</strong> {backendStatus.message}
                  </div>
                  <div className={`badge bg-${backendStatus.success ? 'success' : 'danger'}`}>
                    {backendStatus.success ? 'Online' : 'Offline'}
                  </div>
                </div>
                
                {backendStatus.data && (
                  <div className="mt-2 small">
                    <div>PHP: {backendStatus.data.php_version}</div>
                    <div>Database: {backendStatus.data.database?.status}</div>
                  </div>
                )}
              </Alert>
            )}

            {/* Login Card */}
            <Card className="shadow">
              <Card.Header className="bg-primary text-white text-center py-3">
                <h4 className="mb-0">
                  <i className="fas fa-university me-2"></i>
                  College Admin Login
                </h4>
              </Card.Header>
              <Card.Body className="p-4">
                {error && (
                  <Alert variant="danger" dismissible onClose={() => setError('')}>
                    <i className="fas fa-exclamation-circle me-2"></i>
                    {error}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-3">
                    <Form.Label>Username</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Enter password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={loading}
                    />
                  </Form.Group>

                  <Button 
                    type="submit" 
                    variant="primary" 
                    size="lg" 
                    className="w-100"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Logging in...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sign-in-alt me-2"></i>
                        Login
                      </>
                    )}
                  </Button>
                </Form>

                {/* Test Credentials */}
                <div className="mt-4 text-center">
                  <div className="card bg-light">
                    <div className="card-body p-3">
                      <small className="text-muted">
                        <strong>Test Credentials:</strong><br />
                        Username: <code>admin</code><br />
                        Password: <code>admin123</code>
                      </small>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>

            {/* Debug Info */}
            <div className="mt-3 text-center">
              <small className="text-muted">
                <button 
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => {
                    console.log('LocalStorage:', localStorage);
                    console.log('Current user:', authAPI.getCurrentUser());
                  }}
                >
                  Debug Info
                </button>
              </small>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AdminLogin;